// Help / hướng dẫn sử dụng cho admin của License Server.
// Trang nội bộ, không có search/filter — chỉ là tài liệu tĩnh dạng accordion.

import { Alert, Card, Collapse, Steps, Tag, Typography } from 'antd';
import {
  ApiOutlined,
  KeyOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

export function HelpPage() {
  return (
    <div style={{ maxWidth: 960 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <QuestionCircleOutlined /> Hướng dẫn sử dụng
      </Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="License Server là backend riêng của team bán hàng VR360 Tools."
        description="Khách hàng KHÔNG truy cập trang này. Chỉ owner + support đăng nhập để quản lý license."
      />

      <Card title="Quy trình tổng quát" style={{ marginBottom: 16 }}>
        <Steps
          direction="vertical"
          current={-1}
          items={[
            {
              title: 'Tạo Customer',
              description: 'Khi có khách mua, vào tab Customers → Tạo customer mới (tên công ty, người liên hệ, email, SĐT, MST).',
              icon: <TeamOutlined />,
            },
            {
              title: 'Issue License',
              description: 'Vào tab Licenses → Issue license → chọn customer, plan, features, hạn dùng. Hệ thống sinh KEY + SECRET.',
              icon: <KeyOutlined />,
            },
            {
              title: 'Gửi key cho khách',
              description: 'Copy KEY (KHÔNG cần secret) và gửi cho khách qua email/Zalo. Khách nhập vào trang Activation của VR360 Tools.',
              icon: <SafetyCertificateOutlined />,
            },
            {
              title: 'Theo dõi',
              description: 'Status chuyển từ pending → active sau khi khách activate lần đầu. VR360 Tools tự ping verify mỗi 6 giờ.',
              icon: <ApiOutlined />,
            },
            {
              title: 'Gia hạn / Thu hồi',
              description: 'Khi khách renew: Extend +N ngày (không cần đổi key). Khi refund / vi phạm: Revoke (instance sẽ dừng trong vòng 6h).',
              icon: <LockOutlined />,
            },
          ]}
        />
      </Card>

      <Collapse
        defaultActiveKey={['customer']}
        items={[
          {
            key: 'customer',
            label: <Text strong>Quản lý Customer</Text>,
            children: (
              <>
                <Paragraph>
                  Customer = công ty mua VR360 Tools. Một customer có thể có nhiều license
                  (mua nhiều site, mua plan khác nhau, hoặc các đợt renew).
                </Paragraph>
                <ul>
                  <li>
                    <Text strong>Tạo:</Text> nhập tên công ty, người liên hệ, email, SĐT.
                    Email + MST optional (cho invoice).
                  </li>
                  <li>
                    <Text strong>Sửa:</Text> click icon bút chì. Cập nhật bất kỳ field nào.
                  </li>
                  <li>
                    <Text strong>Xoá:</Text> click icon thùng rác. Không xoá được nếu
                    customer còn license — phải thu hồi license trước. (HTTP 409 nếu cố xoá).
                  </li>
                  <li>
                    <Text strong>Tìm kiếm:</Text> ô search tìm theo tên / contactName / email.
                  </li>
                </ul>
              </>
            ),
          },
          {
            key: 'issue',
            label: <Text strong>Issue License — Lưu ý quan trọng</Text>,
            children: (
              <>
                <Alert
                  type="warning"
                  showIcon
                  message="Secret chỉ hiển thị 1 LẦN duy nhất sau khi issue. Phải copy ngay."
                  style={{ marginBottom: 12 }}
                />
                <Paragraph>
                  Issue license sinh ra 2 giá trị:
                </Paragraph>
                <ul>
                  <li>
                    <Text code>key</Text> — format <Text code>VR36-XXXX-XXXX-XXXX-XXXX</Text>,
                    là cái khách nhập vào VR360 Tools. Có thể xem lại bất kỳ lúc nào trong table.
                  </li>
                  <li>
                    <Text code>secret</Text> — 256-bit hex, dùng để VR360 Tools ký HMAC mọi
                    request verify. Server giữ trong DB (encrypted khi rest) và CHỈ trả về 1 lần
                    qua endpoint <Text code>/api/v1/activate</Text>. Không hiển thị lại trong UI
                    sau khi đóng modal.
                  </li>
                </ul>
                <Paragraph>
                  Các field:
                </Paragraph>
                <ul>
                  <li><Text strong>Plan:</Text> basic / pro / enterprise — chỉ là nhãn cho billing.</li>
                  <li>
                    <Text strong>Features:</Text> array của feature flags (<Tag color="blue">whiteLabel</Tag>,{' '}
                    <Tag color="blue">multiUser</Tag>, ...). VR360 Tools đọc và gate UI tương ứng.
                  </li>
                  <li>
                    <Text strong>Domain lock:</Text> optional. Khi set, license chỉ activate
                    được trên domain đúng → chống share key.
                  </li>
                  <li>
                    <Text strong>Max instances:</Text> số server tối đa được activate cùng key.
                    Default 1.
                  </li>
                  <li>
                    <Text strong>Expires at:</Text> ngày hết hạn. Sau ngày này status tự chuyển
                    <Tag color="red">expired</Tag>.
                  </li>
                  <li>
                    <Text strong>Payment:</Text> unpaid / paid / refunded — chỉ dùng cho audit.
                    Không ảnh hưởng đến verify.
                  </li>
                </ul>
              </>
            ),
          },
          {
            key: 'extend',
            label: <Text strong>Gia hạn (Extend)</Text>,
            children: (
              <>
                <Paragraph>
                  Khi khách renew, dùng <Text strong>Extend</Text> (icon refresh) thay vì
                  issue license mới:
                </Paragraph>
                <ul>
                  <li>Nhập số ngày cộng thêm (mặc định 365).</li>
                  <li>
                    Nếu license đang còn hạn: cộng vào <Text code>expiresAt</Text> hiện tại.
                  </li>
                  <li>
                    Nếu đã expired: cộng từ <Text code>now</Text>; status tự chuyển về
                    <Tag color="green">active</Tag>.
                  </li>
                  <li>Khách KHÔNG cần đổi key — instance sẽ tự nhận hạn mới ở lần verify kế tiếp.</li>
                </ul>
              </>
            ),
          },
          {
            key: 'revoke',
            label: <Text strong>Thu hồi (Revoke)</Text>,
            children: (
              <>
                <Paragraph>
                  Dùng khi: refund, vi phạm điều khoản, key bị lộ, ngưng hợp đồng.
                </Paragraph>
                <ul>
                  <li>Click icon X đỏ ở row license → nhập lý do (bắt buộc, lưu vào audit log).</li>
                  <li>
                    Status chuyển <Tag color="volcano">revoked</Tag>, <Text code>revokedAt</Text>{' '}
                    + <Text code>revokedReason</Text> được lưu.
                  </li>
                  <li>
                    Instance VR360 Tools còn ping verify trong vòng 6h sẽ nhận status revoked → tự
                    block UI mutations (license-check middleware).
                  </li>
                  <li>KHÔNG thể un-revoke. Nếu muốn cho khách dùng lại, phải Issue license mới.</li>
                </ul>
              </>
            ),
          },
          {
            key: 'api',
            label: <Text strong>Public API (cho VR360 Tools)</Text>,
            children: (
              <>
                <Paragraph>
                  Hai endpoint duy nhất khách (VR360 Tools instances) gọi:
                </Paragraph>
                <ul>
                  <li>
                    <Text code>POST /api/v1/activate</Text> — không cần signature (chicken-and-egg).
                    Trả về <Text code>licenseSecret</Text> exactly once để instance lưu.
                  </li>
                  <li>
                    <Text code>POST /api/v1/verify</Text> — yêu cầu HMAC-SHA256 signature trong
                    header <Text code>X-VR360-Signature</Text> + timestamp ±5 phút.
                  </li>
                </ul>
                <Paragraph>
                  Rate limit per IP: activate 30/min, verify 60/min. Mọi call (success + fail) đều
                  log vào <Text code>check_logs</Text> collection (TTL 90 ngày).
                </Paragraph>
                <Paragraph type="secondary">
                  Bạn không cần làm gì ở đây — VR360 Tools instances gọi tự động. Phần này chỉ để
                  reference khi debug.
                </Paragraph>
              </>
            ),
          },
          {
            key: 'hmac',
            label: <Text strong>HMAC scheme (technical reference)</Text>,
            children: (
              <>
                <Paragraph>
                  VR360 Tools tính signature như sau:
                </Paragraph>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                  }}
                >{`payload   = \`\${timestampMs}.\${JSON.stringify(body)}\`
signature = HMAC-SHA256(license.secret, payload).hex

Headers:
  X-VR360-Timestamp: <timestampMs>
  X-VR360-Signature: <signature hex>`}</pre>
                <Paragraph type="secondary">
                  Server verify bằng timing-safe compare. Nếu mismatch hoặc timestamp lệch quá 5
                  phút → 401 <Text code>INVALID_SIGNATURE</Text> và log với{' '}
                  <Text code>signatureValid=false</Text>.
                </Paragraph>
              </>
            ),
          },
          {
            key: 'roles',
            label: <Text strong>Roles: Owner vs Support</Text>,
            children: (
              <ul>
                <li>
                  <Tag color="purple">owner</Tag> — toàn quyền: issue / revoke / extend /
                  regenerate-key license. Tạo qua <Text code>seed-owner</Text> script lúc deploy.
                </li>
                <li>
                  <Tag>support</Tag> — chỉ xem + sửa customer + xem license. KHÔNG được
                  thực hiện hành động ảnh hưởng tiền (issue/revoke/extend). Owner cấp thủ công qua DB.
                </li>
              </ul>
            ),
          },
          {
            key: 'faq',
            label: <Text strong>FAQ</Text>,
            children: (
              <ul>
                <li>
                  <Text strong>Q: Lỡ đóng modal trước khi copy secret?</Text>
                  <br />
                  A: Không lấy lại được trong UI. Có thể read trực tiếp từ MongoDB (field{' '}
                  <Text code>licenses.secret</Text>) hoặc dùng <Text code>regenerate-key</Text>{' '}
                  nếu khách chưa activate. Nếu khách đã activate, gọi <Text code>/api/v1/activate</Text>{' '}
                  cùng instanceId sẽ idempotent trả lại secret cũ.
                </li>
                <li>
                  <Text strong>Q: Khách báo "License expired" nhưng tôi đã extend?</Text>
                  <br />
                  A: VR360 Tools cache status 6h. Khách restart server hoặc bấm "Kiểm tra ngay"
                  trong tab License để force refresh.
                </li>
                <li>
                  <Text strong>Q: Đổi key (regenerate)?</Text>
                  <br />
                  A: Endpoint có sẵn nhưng UI chưa hiển thị (Phase L5 defer). Dùng cURL nếu cần:
                  <Text code>POST /api/admin/licenses/:id/regenerate-key</Text>. Secret giữ
                  nguyên, chỉ key đổi → khách phải nhập key mới vào VR360 Tools.
                </li>
                <li>
                  <Text strong>Q: Backup database?</Text>
                  <br />
                  A: <Text code>docker compose exec mongodb mongodump --archive --gzip &gt; backup.gz</Text>.
                  Restore: <Text code>mongorestore --archive --gzip &lt; backup.gz</Text>.
                </li>
              </ul>
            ),
          },
        ]}
      />
    </div>
  );
}
